import Modal from "../../app/universalComponents/Modal";
import Button from "../../app/universalComponents/Button";
import SectionLabel from "../../app/universalComponents/SectionLabel";
import Alert from "../../app/universalComponents/Alert";
import ImageUpload from "../../app/universalComponents/ImageUpload";
import Input from "../../app/universalComponents/Input";

import { createEvent, updateEvent, uploadPoster } from "./createEditApi";
import { useState, useRef, useEffect } from "react";

function getInitialValues(event) {
    return {
        title: event?.title ?? "",
        poster: event?.poster ?? "",
        description: event?.description ?? "",
        venue: event?.venue ?? "",
        minAge: event?.age ?? "",
        startDate: event?.start_date ?? "",
        startTime: event?.start_time?.slice(0, 5) ?? "",
        endDate: event?.end_date ?? "",
        endTime: event?.end_time?.slice(0, 5) ?? "",
        slug: event?.slug ?? "",
    };
}

export default function EventInfoForm({ activeEvent, mode, onClose }) {
    const [error, setError] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState(null);
    const [values, setValues] = useState(() => getInitialValues(activeEvent));
    const [posterResetVersion, setPosterResetVersion] = useState(0);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        setValues(getInitialValues(activeEvent));
        setIsDirty(false);
        setError(null);
        setSelectedPoster(null);
        setPosterResetVersion((current) => current + 1);
    }, [activeEvent]);

    useEffect(() => {
        if (!error) {
            return;
        }
        requestAnimationFrame(() => {
            scrollContainerRef.current?.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        });
    }, [error]);

    function handleChange(event) {
        const { name, value } = event.target;
        setValues((current) => ({
            ...current,
            [name]: value,
        }));
        setIsDirty(true);
    }

    function handlePosterChange(file) {
        setSelectedPoster(file);
        setIsDirty(true);
    }

    function handleDiscard() {
        if (isSubmitting) return;

        if (mode === "create") {
            setValues(getInitialValues());
        } else {
            setValues(getInitialValues(activeEvent));
        }
        setIsDirty(false);
        setError(null);
        setSelectedPoster(null);
        setPosterResetVersion((current) => current + 1);

        scrollContainerRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (isSubmitting) return;

        setError(null);
        setIsSubmitting(true);

        try {
            let eventId;

            if (mode === "create") {
                const createdEvent = await createEvent(values);
                eventId = createdEvent.id;
                if (!eventId) {
                    throw new Error("The server did not return the new event ID.");
                }
            } else {
                if (!activeEvent?.id) {
                    throw new Error("No event was selected for editing.");
                }
                eventId = activeEvent.id;
                await updateEvent(eventId, values);
            }

            if (selectedPoster) {
                await uploadPoster(eventId, selectedPoster);
            }

            window.location.reload();
        } catch (error) {
            console.error("Failed to save event:", error);
            const detail = error.response?.data?.detail;
            setIsSubmitting(false);
            setError(
                typeof detail === "string"
                    ? detail
                    : error.response?.data?.message ||
                    error.message ||
                    "The event could not be saved."
            );
        }
    }

    return (
        <Modal isOpen={true} onClose={onClose} className="flex flex-col">
            <header className="relative text-gray-500 flex items-center justify-center border-b border p-2">
                <h2 className="text-sm">
                    {mode === "create" ? "Create Event" : "Edit Event Information"}
                </h2>
                <button type="button" onClick={onClose} className="absolute right-0 flex h-8 w-8 items-center justify-center">
                    ✕
                </button>
            </header>

            <form onSubmit={handleSubmit} id="eventDetails" ref={scrollContainerRef} className="flex-1 space-y-4 overflow-y-scroll p-4 md:px-8 py-4">
                {error && (
                    <Alert className="w-full rounded-sm text-center" tone="negative">
                        {error}
                    </Alert>
                )}

                <div>
                    <SectionLabel>Basics</SectionLabel>

                    <fieldset className="flex flex-col gap-4 rounded-sm border p-4 md:flex-row md:items-stretch">
                        <ImageUpload label="Poster" name="poster" key={posterResetVersion} initialImage={values.poster ?? null} onChange={handlePosterChange} className="w-48 self-center md:self-stretch">
                            <p className="mt-1 text-xs text-gray-500">🛈 2:3 ratio often works best.</p>
                        </ImageUpload>

                        <div className="flex flex-col space-y-4 md:w-96">
                            <Input label="Title" name="title" required value={values.title} placeholder="My Event" onChange={handleChange} />
                            <Input label="Description" name="description" required isLarge value={values.description} placeholder="It will be fun" onChange={handleChange} className="flex-1" />
                            <Input label="Venue" name="venue" required value={values.venue} placeholder="My Venue" onChange={handleChange} />
                            <Input label="Age Requirement" name="minAge" placeholder="18+ only" value={values.minAge} onChange={handleChange}>
                                <p className="mt-1 text-xs text-gray-500">🛈 Add any age restrictions.</p>
                            </Input>
                        </div>
                    </fieldset>
                </div>

                <div>
                    <SectionLabel>Schedule</SectionLabel>

                    <fieldset className="grid grid-cols-5 gap-4 rounded-sm border p-4">
                        <Input label="Start Date" name="startDate" type="date" required value={values.startDate} onChange={handleChange} className="col-span-3" />
                        <Input label="Start Time" name="startTime" type="time" required value={values.startTime} onChange={handleChange} className="col-span-2" />
                        <Input label="End Date" name="endDate" type="date" value={values.endDate} onChange={handleChange} className="col-span-3" />
                        <Input label="End Time" name="endTime" type="time" value={values.endTime} onChange={handleChange} className="col-span-2" />
                    </fieldset>
                </div>

                {mode === "create" &&
                    <div>
                        <SectionLabel>Sharing</SectionLabel>
                        <fieldset className="rounded-sm border p-4">
                            <Input label="Link" prefix="tickets.npsolved.com/events/" name="slug" placeholder="my-event-2026" required value={values.slug} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" onChange={handleChange}>
                                <p className="mt-1 text-xs text-gray-500">🛈 This Link cannot be changed after you create the event. Use lowercase letters, numbers, and hyphens only.</p>
                            </Input>
                        </fieldset>
                    </div>
                }
            </form>

            <footer className="flex items-center justify-between border-t py-2 px-4 md:px-8">
                <p className="text-sm text-gray-500">
                    required{" "}
                    <span className="text-red-500 text-base">*</span>
                </p>

                <div className="flex items-center gap-2">
                    {(isDirty && mode !== "create") && (
                        <span className="text-amber-500">
                            ● <span className="hidden pr-4 text-sm text-gray-500 md:inline">Changes Detected</span>
                        </span>
                    )}

                    <Button type="button" variant="secondary" disabled={!isDirty} onClick={handleDiscard} className="text-sm">
                        Clear Changes
                    </Button>

                    <Button type="submit" form="eventDetails" disabled={(!isDirty || isSubmitting)} className="text-sm">
                        {isSubmitting ? "Saving" : "Save"}
                    </Button>
                </div>
            </footer>
        </Modal>
    );
}